import {
  faArrowLeft,
  faArrowRight,
  faCheck,
  faRotateLeft,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Badge,
  Button,
  Grid,
  Icon,
  Inline,
  Message,
} from "@intility/bifrost-react";
import Drawer from "@intility/bifrost-react/Drawer";
import Menu from "@intility/bifrost-react/Menu";
import RadioCard from "@intility/bifrost-react/RadioCard";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  createResourceType,
  createUsage,
  deleteUsage,
  getBlueprints,
  listResourceTypes,
  updateUsage,
  validateUsagePlacement,
} from "~/api/client";
import type {
  BlueprintNode,
  BlueprintResponse,
  CreateResourceTypeRequest,
  PlatformName,
  ResourceType,
} from "~/api/fga-types";
import { usePlatformNames } from "~/api/platforms";
import { PageLayout } from "~/components/layout/PageLayout";
import BlueprintCanvas, { type BlueprintSelection } from "./BlueprintCanvas";
import BlueprintDrawer from "./BlueprintDrawer";
import BlueprintLearn, {
  LEARN_STEPS,
  LearnFirstResourceDrawer,
} from "./BlueprintLearn";
import BlueprintReview from "./BlueprintReview";
import SeedPanel from "./SeedPanel";
import {
  EMPTY_STAGED,
  hasStagedChanges,
  nextTempId,
  runCommit,
  type StagedChanges,
  stageDelete,
  stepLabel,
} from "./stagedCommit";
import "./BlueprintBuilder.css";

function findNode(
  nodes: BlueprintNode[],
  usageId: string,
): BlueprintNode | null {
  for (const n of nodes) {
    if (n.usageId === usageId) return n;
    const found = findNode(n.children, usageId);
    if (found) return found;
  }
  return null;
}

function buildWorkingBlueprint(
  server: BlueprintResponse,
  changes: StagedChanges,
  types: ResourceType[],
): BlueprintResponse {
  const typeMap = new Map(types.map((t) => [t.id, t]));
  const deletedSet = new Set(changes.deletes);

  function markTree(nodes: BlueprintNode[]): BlueprintNode[] {
    return nodes.map((n) => {
      const edit = changes.edits.find((e) => e.usageId === n.usageId);
      return {
        ...n,
        qualifiedName: edit?.qualifiedName ?? n.qualifiedName,
        children: markTree(n.children),
      };
    });
  }

  const platforms = server.platforms.map((p) => ({
    ...p,
    tree: markTree(p.tree),
  }));

  for (const add of changes.adds) {
    if (deletedSet.has(add.tempId)) continue;
    const type = typeMap.get(add.typeId);
    if (!type) continue;
    const newNode: BlueprintNode = {
      usageId: add.tempId,
      type,
      qualifiedName: add.qualifiedName,
      children: [],
    };
    const platform = platforms.find((p) => p.platform === add.platformSlug);
    if (!platform) continue;
    if (add.parentUsageId === null) {
      platform.tree.push(newNode);
    } else {
      const parent = findNode(platform.tree, add.parentUsageId);
      if (parent) {
        parent.children.push(newNode);
      }
    }
  }

  return { ...server, platforms };
}

type Phase = "learn" | "build" | "review";

function stepVariant(
  stepIndex: number,
  currentStep: number,
  phase: Phase,
): "default" | "active" | "completed" | "incomplete" {
  if (phase !== "learn") return "completed";
  if (stepIndex < currentStep) return "completed";
  if (stepIndex === currentStep) return "active";
  if (stepIndex <= currentStep + 1) return "incomplete";
  return "default";
}

function phaseVariant(
  target: Phase,
  current: Phase,
): "default" | "active" | "completed" {
  const order: Phase[] = ["learn", "build", "review"];
  const ti = order.indexOf(target);
  const ci = order.indexOf(current);
  if (ti < ci) return "completed";
  if (ti === ci) return "active";
  return "default";
}

// A blueprint exists when its Platform exists (ADR-0059), so the builder
// starts from one of the caller's Platforms. A new Platform is registered on
// the Platforms page, not here.
function PlatformSelector({
  platforms,
  onSelect,
  error,
}: {
  platforms: PlatformName[];
  onSelect: (slug: string) => void;
  error: string | null;
}) {
  const navigate = useNavigate();
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  return (
    <PageLayout
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Platforms", to: "/platforms" },
        { label: "Blueprint builder" },
      ]}
      title="Blueprint builder"
      description="Build the resource hierarchy for your platform step by step. Start by choosing one of your platforms."
    >
      <Grid gap={24} style={{ maxWidth: 640, marginInline: "auto" }}>
        {error && (
          <Message state="alert" header="Error">
            {error}
          </Message>
        )}

        {platforms.length === 0 ? (
          <p className="bf-p" style={{ margin: 0 }}>
            You have no platforms yet. A Platform gets its blueprint when it is
            registered.
          </p>
        ) : (
          <Grid gap={8}>
            {platforms.map((p) => (
              <RadioCard
                key={p.slug}
                name="platform"
                label={p.displayName}
                checked={selectedSlug === p.slug}
                onChange={() => setSelectedSlug(p.slug)}
              >
                <code className="bf-small">{p.slug}</code>
              </RadioCard>
            ))}
          </Grid>
        )}

        <Inline>
          <Button variant="basic" onClick={() => navigate("/platforms")}>
            Back
          </Button>
          <Button variant="basic" onClick={() => navigate("/platforms/new")}>
            Register a platform
          </Button>
          <Button
            state={selectedSlug ? undefined : "inactive"}
            onClick={() => selectedSlug && onSelect(selectedSlug)}
          >
            Start building
          </Button>
        </Inline>
      </Grid>
    </PageLayout>
  );
}

export default function BlueprintBuilder() {
  const navigate = useNavigate();
  const { platform: platformParam } = useParams();
  // The URL carries the bare Platform slug, which is also the blueprint key.
  const preselectedPlatform = platformParam ?? null;

  const [blueprint, setBlueprint] = useState<BlueprintResponse | null>(null);
  const platforms = usePlatformNames();
  const [types, setTypes] = useState<ResourceType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [activePlatform, setActivePlatform] = useState<string | null>(
    preselectedPlatform,
  );

  useEffect(() => {
    if (!platformParam) setActivePlatform(null);
  }, [platformParam]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selection, setSelection] = useState<BlueprintSelection | null>(null);
  // "inspect" = node clicked (view/edit/delete); "add" = "+" clicked (add child).
  const [drawerMode, setDrawerMode] = useState<"inspect" | "add">("inspect");

  const [phase, setPhase] = useState<Phase>("learn");
  const [learnStep, setLearnStep] = useState(0);
  // Types created during this session, shown as feedback in the create-type
  // drawer and as quick-pick buttons in the "Add your first resource" drawer.
  const [createdTypes, setCreatedTypes] = useState<ResourceType[]>([]);
  const [stagedChanges, setStagedChanges] =
    useState<StagedChanges>(EMPTY_STAGED);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [bp, ts] = await Promise.all([
        getBlueprints(),
        listResourceTypes(),
      ]);
      setBlueprint(bp);
      setTypes(ts.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const workingBlueprint = useMemo(
    () =>
      blueprint && hasStagedChanges(stagedChanges)
        ? buildWorkingBlueprint(blueprint, stagedChanges, types)
        : blueprint,
    [blueprint, stagedChanges, types],
  );

  const deletedNodeIdSet = useMemo(
    () => new Set(stagedChanges.deletes),
    [stagedChanges.deletes],
  );

  const stagedNodeIdSet = useMemo(
    () => new Set(stagedChanges.adds.map((a) => a.tempId)),
    [stagedChanges.adds],
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSelect = (id: string | null, sel: BlueprintSelection | null) => {
    setSelectedId(id);
    setSelection(sel);
    setDrawerMode("inspect");
  };

  const handleAddChild = (_id: string, sel: BlueprintSelection | null) => {
    // "+" is an add action, not a selection — don't outline the node. The
    // drawer's "Add to X" header conveys which node we're adding under.
    setSelectedId(null);
    setSelection(sel);
    setDrawerMode("add");
  };

  const closeDrawer = () => {
    setSelectedId(null);
    setSelection(null);
  };

  const handleFinish = () => {
    // /blueprints/:slug redirects to the Blueprint tab of the platform page.
    navigate(activePlatform ? `/blueprints/${activePlatform}` : "/platforms");
  };

  const siblingTypeIdsFor = useCallback(
    (platformSlug: string, parentUsageId: string | null): Set<string> => {
      const p = workingBlueprint?.platforms.find(
        (pf) => pf.platform === platformSlug,
      );
      if (!p) return new Set();
      const siblings =
        parentUsageId === null
          ? p.tree
          : (findNode(p.tree, parentUsageId)?.children ?? []);
      return new Set(siblings.map((n) => n.type.id));
    },
    [workingBlueprint],
  );

  const availableTypes = useMemo<ResourceType[]>(() => {
    if (!selection) return [];
    if (selection.kind === "platform") {
      const siblings = siblingTypeIdsFor(selection.platformSlug, null);
      return types.filter((t) => !siblings.has(t.id));
    }
    if (selection.kind === "resource") {
      const siblings = siblingTypeIdsFor(
        selection.platformSlug,
        selection.usageId,
      );
      return types.filter((t) => !siblings.has(t.id));
    }
    return [];
  }, [selection, siblingTypeIdsFor, types]);

  // The "Add your first resource" learn step guides the user to click the
  // platform node; the drawer opens there too (not just in the build phase).
  const isFirstResourceStep = phase === "learn" && learnStep === 3;
  const hasResource =
    (workingBlueprint?.platforms.find((p) => p.platform === activePlatform)
      ?.tree.length ?? 0) > 0;
  const pulseId =
    isFirstResourceStep && !hasResource ? `platform:${activePlatform}` : null;
  const drawerOpen =
    selection !== null &&
    (phase === "build" || (isFirstResourceStep && drawerMode === "add"));

  const handleSelectPlatform = (slug: string) => {
    setError(null);
    setActivePlatform(slug);
    navigate(`/blueprints/builder/${slug}`, { replace: true });
  };

  const handleAddUsage = async (
    platformSlug: string,
    parentUsageId: string | null,
    typeId: string,
    qualifiedName?: string,
  ) => {
    if (phase === "build") {
      const type = types.find((t) => t.id === typeId);
      setStagedChanges((prev) => ({
        ...prev,
        adds: [
          ...prev.adds,
          {
            tempId: nextTempId(),
            platformSlug,
            parentUsageId,
            typeId,
            qualifiedName: qualifiedName || type?.slug || typeId,
          },
        ],
      }));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const type = types.find((t) => t.id === typeId);
      await createUsage(platformSlug, {
        typeId,
        parentUsageId: parentUsageId ?? undefined,
        qualifiedName: qualifiedName || type?.slug || typeId,
      });
      await loadAll();
      if (phase === "learn" && learnStep === 3) {
        setLearnStep(4);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Rethrow so the learn-phase drawer can stay open on failure (the build
      // phase returns before this try block, so staged adds never throw).
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateUsage = async (usageId: string, qualifiedName: string) => {
    // Staged nodes have no server id yet, so an edit on one is staged too
    // (also in the review phase) and folded into its add at commit time.
    if (phase === "build" || stagedNodeIdSet.has(usageId)) {
      setStagedChanges((prev) => ({
        ...prev,
        edits: [
          ...prev.edits.filter((e) => e.usageId !== usageId),
          { usageId, qualifiedName },
        ],
      }));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updateUsage(usageId, { qualifiedName });
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteUsage = async (usageId: string) => {
    // Staged nodes have no server id, so a delete on one is staged in any
    // phase. Staged descendants go with their parent, and a server delete
    // takes any staged descendants below it.
    if (phase === "build" || stagedNodeIdSet.has(usageId)) {
      setStagedChanges((prev) => stageDelete(prev, usageId));
      closeDrawer();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deleteUsage(usageId);
      closeDrawer();
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCreateType = async (
    req: CreateResourceTypeRequest,
  ): Promise<ResourceType | undefined> => {
    setBusy(true);
    setError(null);
    try {
      const newType = await createResourceType(req);
      await loadAll();
      setCreatedTypes((prev) => [...prev, newType]);
      return newType;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Prechecks a placement for a not-yet-created type before it's created, so a
  // placement that would be rejected never leaves an orphan type behind.
  const handleValidatePlacement = async (
    platformSlug: string,
    parentUsageId: string | null,
    qualifiedName: string,
    typeNameRegex: string | undefined,
    typeIsStatic: boolean,
  ) => {
    setBusy(true);
    setError(null);
    try {
      await validateUsagePlacement(platformSlug, {
        parentUsageId,
        qualifiedName,
        typeNameRegex,
        typeIsStatic,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  // Resolves a server usage id to its qualified name, for commit error
  // messages that name the node instead of a URL.
  const labelForUsageId = (usageId: string): string => {
    if (!workingBlueprint) return usageId;
    for (const p of workingBlueprint.platforms) {
      const node = findNode(p.tree, usageId);
      if (node) return node.qualifiedName ?? usageId;
    }
    return usageId;
  };

  const handleCommit = async () => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await runCommit(
        stagedChanges,
        { createUsage, updateUsage, deleteUsage },
        (staged) => setStagedChanges(staged),
      );
      if (outcome.failedStep) {
        // Steps that landed are already gone from the staged state, so a
        // retry sends only what is left. Reload so landed nodes appear with
        // their server ids.
        await loadAll();
        setStagedChanges(outcome.staged);
        setError(
          `Commit stopped at ${stepLabel(outcome.failedStep, labelForUsageId)}: ${
            outcome.error instanceof Error
              ? outcome.error.message
              : String(outcome.error)
          }`,
        );
        return;
      }
      setStagedChanges(EMPTY_STAGED);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = () => {
    setStagedChanges(EMPTY_STAGED);
  };

  if (!activePlatform) {
    return (
      <PlatformSelector
        platforms={platforms}
        onSelect={handleSelectPlatform}
        error={error}
      />
    );
  }

  return (
    <div className="bp-builder-layout">
      <div className="bp-builder-body">
        <nav className="bp-left-nav">
          <div className="bp-left-nav-section">
            <span className="bp-left-nav-heading">Learn</span>
            <Menu>
              {LEARN_STEPS.map((s, i) => (
                <button
                  key={s.title}
                  type="button"
                  className={
                    phase === "learn" && learnStep === i ? "active" : ""
                  }
                  onClick={() => {
                    setPhase("learn");
                    setLearnStep(i);
                  }}
                >
                  <Menu.Item>
                    <Icon.Step
                      variant={stepVariant(i, learnStep, phase)}
                      marginRight
                    />
                    {s.title}
                  </Menu.Item>
                </button>
              ))}
            </Menu>
          </div>

          <div className="bp-left-nav-section">
            <span className="bp-left-nav-heading">Build</span>
            <Menu>
              <button
                type="button"
                className={phase === "build" ? "active" : ""}
                onClick={() => setPhase("build")}
              >
                <Menu.Item>
                  <Icon.Step
                    variant={phaseVariant("build", phase)}
                    marginRight
                  />
                  Add resources
                </Menu.Item>
              </button>
            </Menu>
          </div>

          <div className="bp-left-nav-section">
            <span className="bp-left-nav-heading">Review</span>
            <Menu>
              <button
                type="button"
                className={phase === "review" ? "active" : ""}
                onClick={() => setPhase("review")}
              >
                <Menu.Item>
                  <Icon.Step
                    variant={phaseVariant("review", phase)}
                    marginRight
                  />
                  Summary
                </Menu.Item>
              </button>
            </Menu>
          </div>
        </nav>

        <div className="bp-main">
          <div className="bp-center">
            {error && (
              <Message
                state="alert"
                header="Error"
                style={{ margin: "0 0 12px" }}
              >
                {error}
              </Message>
            )}

            {phase === "learn" && (
              <div className="bp-step-header">
                <BlueprintLearn
                  step={learnStep}
                  onCreateType={handleCreateType}
                  busy={busy}
                  createdTypes={createdTypes}
                />
              </div>
            )}

            {phase === "build" && (
              <div className="bp-step-header">
                <h2 className="bf-h2">Build your blueprint</h2>
                <p
                  className="bf-p"
                  style={{ color: "var(--bfc-base-c-2)", margin: 0 }}
                >
                  Hover a node and click <strong>+</strong> to add a child.
                  Click a node to edit or delete it.
                </p>
                {hasStagedChanges(stagedChanges) && (
                  <Message
                    state="warning"
                    header="Pending changes"
                    style={{ marginTop: 12 }}
                  >
                    <Grid gap={4}>
                      {stagedChanges.adds.length > 0 && (
                        <span>
                          <Badge state="success">new</Badge>{" "}
                          {stagedChanges.adds.length} node
                          {stagedChanges.adds.length !== 1 ? "s" : ""} to add
                        </span>
                      )}
                      {stagedChanges.edits.length > 0 && (
                        <span>
                          <Badge state="neutral">edit</Badge>{" "}
                          {stagedChanges.edits.length} node
                          {stagedChanges.edits.length !== 1 ? "s" : ""} to
                          update
                        </span>
                      )}
                      {stagedChanges.deletes.length > 0 && (
                        <span>
                          <Badge state="alert">delete</Badge>{" "}
                          {stagedChanges.deletes.length} node
                          {stagedChanges.deletes.length !== 1 ? "s" : ""} to
                          remove
                        </span>
                      )}
                    </Grid>
                  </Message>
                )}
              </div>
            )}

            {phase === "review" && (
              <div className="bp-step-header">
                <h2 className="bf-h2">Review</h2>
                <p
                  className="bf-p"
                  style={{ color: "var(--bfc-base-c-2)", margin: 0 }}
                >
                  Verify your blueprint before finishing.
                </p>
              </div>
            )}

            {phase !== "review" && !(phase === "learn" && learnStep === 0) && (
              <div className="bp-canvas-area">
                {workingBlueprint && (
                  <BlueprintCanvas
                    blueprint={workingBlueprint}
                    platforms={platforms}
                    selectedId={selectedId}
                    onSelect={handleSelect}
                    singlePlatform={activePlatform}
                    pulseId={pulseId}
                    onAddChild={handleAddChild}
                    enableAddButton={phase === "build" || isFirstResourceStep}
                    stagedNodeIds={stagedNodeIdSet}
                    deletedNodeIds={deletedNodeIdSet}
                  />
                )}
              </div>
            )}

            {phase === "review" && workingBlueprint && (
              <div className="bp-review-scroll">
                {hasStagedChanges(stagedChanges) && (
                  <div style={{ marginBottom: 16 }}>
                    <Message state="warning" header="Pending changes">
                      <Grid gap={4}>
                        {stagedChanges.adds.length > 0 && (
                          <span>
                            <Badge state="success">new</Badge>{" "}
                            {stagedChanges.adds.length} node
                            {stagedChanges.adds.length !== 1 ? "s" : ""} to add
                          </span>
                        )}
                        {stagedChanges.edits.length > 0 && (
                          <span>
                            <Badge state="neutral">edit</Badge>{" "}
                            {stagedChanges.edits.length} node
                            {stagedChanges.edits.length !== 1 ? "s" : ""} to
                            update
                          </span>
                        )}
                        {stagedChanges.deletes.length > 0 && (
                          <span>
                            <Badge state="alert">delete</Badge>{" "}
                            {stagedChanges.deletes.length} node
                            {stagedChanges.deletes.length !== 1 ? "s" : ""} to
                            remove
                          </span>
                        )}
                      </Grid>
                    </Message>
                  </div>
                )}
                <BlueprintReview
                  blueprint={workingBlueprint}
                  platforms={platforms}
                  singlePlatform={activePlatform}
                  onUpdateUsage={handleUpdateUsage}
                />
                <div style={{ marginTop: 24 }}>
                  <SeedPanel platformSlug={activePlatform} />
                </div>
              </div>
            )}
          </div>

          <div className="bp-bottom-bar">
            <div>
              {phase === "learn" && learnStep > 0 && (
                <Button
                  variant="flat"
                  onClick={() => setLearnStep(learnStep - 1)}
                >
                  <Icon icon={faArrowLeft} marginRight />
                  Previous
                </Button>
              )}
              {phase !== "learn" && (
                <Button variant="flat" onClick={() => setPhase("learn")}>
                  <Icon icon={faArrowLeft} marginRight />
                  Learn
                </Button>
              )}
            </div>
            <Inline align="center">
              {phase === "learn" && learnStep < LEARN_STEPS.length - 1 && (
                <Button onClick={() => setLearnStep(learnStep + 1)}>
                  Next
                  <Icon icon={faArrowRight} marginLeft />
                </Button>
              )}
              {phase === "learn" && learnStep === LEARN_STEPS.length - 1 && (
                <Button variant="filled" onClick={() => setPhase("build")}>
                  Start building
                  <Icon icon={faArrowRight} marginLeft />
                </Button>
              )}
              {phase === "build" && (
                <>
                  {hasStagedChanges(stagedChanges) && (
                    <Inline style={{ marginRight: 8 }}>
                      <Button
                        variant="basic"
                        state="alert"
                        onClick={handleDiscard}
                      >
                        <Icon icon={faRotateLeft} marginRight />
                        Discard
                      </Button>
                      <Button
                        variant="filled"
                        state={busy ? "inactive" : undefined}
                        onClick={handleCommit}
                      >
                        <Icon icon={faCheck} marginRight />
                        Commit changes
                      </Button>
                    </Inline>
                  )}
                  <Button onClick={() => setPhase("review")}>
                    Review
                    <Icon icon={faArrowRight} marginLeft />
                  </Button>
                </>
              )}
              {phase === "review" && (
                <>
                  {hasStagedChanges(stagedChanges) && (
                    <Inline style={{ marginRight: 8 }}>
                      <Button
                        variant="basic"
                        state="alert"
                        onClick={handleDiscard}
                      >
                        <Icon icon={faRotateLeft} marginRight />
                        Discard
                      </Button>
                      <Button
                        state={busy ? "inactive" : undefined}
                        onClick={handleCommit}
                      >
                        <Icon icon={faCheck} marginRight />
                        Commit changes
                      </Button>
                    </Inline>
                  )}
                  <Button
                    variant="filled"
                    state={
                      hasStagedChanges(stagedChanges) ? "inactive" : undefined
                    }
                    onClick={
                      hasStagedChanges(stagedChanges) ? undefined : handleFinish
                    }
                  >
                    <Icon icon={faCheck} marginRight />
                    Finish
                  </Button>
                </>
              )}
            </Inline>
          </div>
        </div>
      </div>

      <Drawer
        header={
          isFirstResourceStep
            ? "Add your first resource"
            : phase === "build" && selection
              ? drawerMode === "add"
                ? `Add to ${
                    selection.kind === "platform"
                      ? selection.displayName
                      : selection.kind === "resource"
                        ? selection.typeName
                        : "company"
                  }`
                : selection.kind === "company"
                  ? "Company"
                  : selection.kind === "platform"
                    ? "Platform"
                    : "Resource"
              : undefined
        }
        isOpen={drawerOpen}
        onRequestClose={closeDrawer}
      >
        {isFirstResourceStep ? (
          <LearnFirstResourceDrawer
            selection={selection}
            availableTypes={availableTypes}
            busy={busy}
            onAddUsage={handleAddUsage}
            onAdded={closeDrawer}
            createdTypes={createdTypes}
          />
        ) : (
          <BlueprintDrawer
            selection={selection}
            availableTypes={availableTypes}
            busy={busy}
            mode={drawerMode}
            onAddUsage={handleAddUsage}
            onUpdateUsage={handleUpdateUsage}
            onDeleteUsage={handleDeleteUsage}
            onCreateType={handleCreateType}
            onValidatePlacement={handleValidatePlacement}
            onShowGuide={() => {
              setPhase("learn");
              setLearnStep(0);
            }}
          />
        )}
      </Drawer>
    </div>
  );
}
